import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BaseModel } from '../model/base.model';

export const JWT_KEY = 'id_token';

export enum AuthenticationMethod {
  None,
  Hmac,
  Jwt
}

export abstract class BaseService<T extends BaseModel> {

  public static multiPartHeader: HttpHeaders = new HttpHeaders({});

  public static basicHeader: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json'
  });

  public API_DOMAIN: string;

  private endPoint: string;

  constructor(
    private http: HttpClient,
    private entityName: string,
    private defaultAuth: AuthenticationMethod = AuthenticationMethod.Jwt,
  ) {
    this.API_DOMAIN = this.endPoint = environment.apiUrl;
  }

  get(path: string, authMethod: AuthenticationMethod = this.defaultAuth): Observable<any> {
    const apiMethod = `api/${this.entityName}${path}`;
    const header = this.makeSecureHeader(authMethod, apiMethod, 'GET');
    return this.http.get(`${this.endPoint}/${apiMethod}`, { headers: header });
  }

  post(path: string, body: any, hasDataOnBody: boolean = false, authMethod: AuthenticationMethod = this.defaultAuth): Observable<any> {
    const apiMethod = `api/${this.entityName}${path}`;
    const header = this.makeSecureHeader(authMethod, apiMethod, 'POST',
      (hasDataOnBody) ? BaseService.multiPartHeader : BaseService.basicHeader);
    return this.http.post(`${this.endPoint}/${apiMethod}`, (hasDataOnBody) ? body : JSON.stringify(body), { headers: header });
  }

  put(path: string, body: any, hasDataOnBody: boolean = false, authMethod: AuthenticationMethod = this.defaultAuth): Observable<any> {
    const apiMethod = `api/${this.entityName}${path}`;
    const header = this.makeSecureHeader(authMethod, apiMethod, 'PUT',
      (hasDataOnBody) ? BaseService.multiPartHeader : BaseService.basicHeader);
    return this.http.put(`${this.endPoint}/${apiMethod}`, (hasDataOnBody) ? body : JSON.stringify(body), { headers: header });
  }

  patch(path: string, body: any, hasDataOnBody: boolean = false, authMethod: AuthenticationMethod = this.defaultAuth): Observable<any> {
    const apiMethod = `api/${this.entityName}${path}`;
    const header = this.makeSecureHeader(authMethod, apiMethod, 'PUT', (hasDataOnBody) ? BaseService.multiPartHeader : BaseService.basicHeader);
    return this.http.patch(`${this.endPoint}/${apiMethod}`, (hasDataOnBody) ? body : JSON.stringify(body), { headers: header });
  }

  delete(path: string, authMethod: AuthenticationMethod = this.defaultAuth): Observable<any> {
    const apiMethod = `api/${this.entityName}${path}`;
    const header = this.makeSecureHeader(authMethod, apiMethod, 'DELETE');
    return this.http.delete(`${this.endPoint}/${apiMethod}`, { headers: header });
  }

  getById(id: string): Observable<T> {
    return this.get(`/${id}`).pipe(map((res: T) => res as T));
  }

  // apply desired authentication method to api calls (JWT, HMAC or no auth).
  private makeSecureHeader(auth: AuthenticationMethod, path: string, http: string, defaultHeader: HttpHeaders = BaseService.basicHeader): HttpHeaders {
    if (typeof auth !== typeof AuthenticationMethod) {
      if (typeof this.defaultAuth === typeof AuthenticationMethod) {
        auth = this.defaultAuth;
      }
    }

    let secureHeader: HttpHeaders = defaultHeader;
    switch (auth) {
      case AuthenticationMethod.None:
        break;
      case AuthenticationMethod.Jwt:
        let jwt = this.createJwtSignature();
        return secureHeader.append('Authorization', `bearer ${jwt}`);
      default:
        break;
    }

    return secureHeader;

  }

  private createJwtSignature(): string | null {
    if (typeof localStorage !== undefined) {
      return localStorage.getItem(JWT_KEY)
    } else {
      return null;
    }
  }
}
